import React, { useState, useRef, useCallback } from "react";

import {
  GoogleMap,
  useJsApiLoader,
  Circle,
  Polyline,
  OverlayView,
  Autocomplete,
} from "@react-google-maps/api";

import {
  Box,
  Fab,
  Stack,
  useMediaQuery,
  useTheme,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import UndoIcon from "@mui/icons-material/Undo";
import DoneIcon from "@mui/icons-material/Done";

import MyLocationIcon from "@mui/icons-material/MyLocation";
import target from "./target.png";

const containerStyle = { width: "100%", height: "100%" };
const center = { lat: 16.4324, lng: 102.8352 };

function MapDrawGoogle({ data }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  const polygonRef = useRef(null);
  const [path, setPath] = useState([]);
  const [currentCenter, setCurrentCenter] = useState(center);
  const [showGuideline, setShowGuideline] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const hasSetCenterListener = useRef(false);

  const onLoad = useCallback((map) => {
    mapRef.current = map;

    if (!hasSetCenterListener.current) {
      hasSetCenterListener.current = true;

      map.addListener("idle", () => {
        const center = map.getCenter();
        setCurrentCenter({ lat: center.lat(), lng: center.lng() });
      });
    }
  }, []);

  const onPlaceChanged = () => {
    if (autocompleteRef.current !== null) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry) {
        const loc = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        setCurrentCenter(loc);
        mapRef.current?.panTo(loc);
        mapRef.current?.setZoom(18);
      }
    }
  };

  const goToMyLocation = () => {
    if (!navigator.geolocation) {
      console.error("non support Geolocation");
      return;
    }

    if (!mapRef.current) {
      console.warn("Google Map not ready");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const myLocation = { lat: latitude, lng: longitude };
        console.log("Mylocation:", myLocation);

        setCurrentCenter(myLocation);
        mapRef.current.panTo(myLocation);
        mapRef.current.setZoom(18);
      },
      (error) => {
        console.error("Can't get location:", error.message);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const handleAddPoint = () => {
    if (mapRef.current) {
      const newPoint = currentCenter;
      const newPath = [...path, newPoint];
      setPath(newPath);

      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }

      polygonRef.current = new window.google.maps.Polygon({
        paths: newPath,
        map: mapRef.current,
        fillColor: "#00FF00",
        fillOpacity: 0.3,
        strokeWeight: 2,
        clickable: false,
        editable: false,
        draggable: false,
      });

      const area = window.google.maps.geometry.spherical.computeArea(
        polygonRef.current.getPath()
      );
      console.log("Area:", area.toFixed(2), "m sqrt");
    }
  };

  const handleUndo = () => {
    if (path.length > 0) {
      const newPath = [...path.slice(0, -1)];
      setPath(newPath);
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }
      if (newPath.length > 0) {
        polygonRef.current = new window.google.maps.Polygon({
          paths: newPath,
          map: mapRef.current,
          fillColor: "#00FF00",
          fillOpacity: 0.3,
          strokeWeight: 2,
          clickable: false,
          editable: false,
          draggable: false,
        });
      }
    }
  };

  const handleConfirm = () => {
    if (path.length >= 3 && window.google && window.google.maps.geometry) {
      const polygon = new window.google.maps.Polygon({ paths: path });
      const area = window.google.maps.geometry.spherical.computeArea(
        polygon.getPath()
      );

      setShowGuideline(false);

      if (data) {
        data({ confirm: true, points: path, area: area.toFixed(2) });
      }
    } else {
      console.warn("Minimun 3 point ไม่งั้นแตกไอเวร");
    }
  };

  return (
    <Box position="relative" width="100%" height="100%">
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={18}
        onLoad={onLoad}
        options={{ gestureHandling: "greedy" }}
      >
        {path.map((point, index) => (
          <React.Fragment key={index}>
            <Circle
              center={point}
              radius={1.5}
              options={{
                fillColor: "#FF0000",
                fillOpacity: 1,
                strokeColor: "#FF0000",
                strokeOpacity: 1,
                strokeWeight: 1,
                clickable: false,
                draggable: false,
                editable: false,
              }}
            />
            <OverlayView
              position={point}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div
                style={{
                  background: "#FF0000",
                  color: "#FFFFFF",
                  border: "2px solid #FF0000",
                  borderRadius: "50%",
                  padding: "4px 7px",
                  fontSize: "12px",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transform: "translate(-50%, -50%)",
                  width: "24px",
                  height: "24px",
                  textAlign: "center",
                }}
              >
                {index + 1}
              </div>
            </OverlayView>
          </React.Fragment>
        ))}

        {showGuideline && path.length > 0 && (
          <Polyline
            path={[path[path.length - 1], currentCenter]}
            options={{
              strokeColor: "#FF0000",
              strokeOpacity: 0.5,
              strokeWeight: 2,
              clickable: false,
              draggable: false,
              editable: false,
              geodesic: true,
              zIndex: 1,
            }}
          />
        )}
      </GoogleMap>
      <Box
        position="absolute"
        top={isMobile ? 60 : 16}
        left="50%"
        sx={{
          transform: "translateX(-50%)",
          zIndex: 2,
          width: isMobile ? "90%" : 400,
        }}
      >
        <Autocomplete
          onLoad={(ref) => (autocompleteRef.current = ref)}
          onPlaceChanged={onPlaceChanged}
        >
          <TextField
            fullWidth
            variant="outlined"
            placeholder="ค้นหาสถานที่..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ backgroundColor: "white", borderRadius: 1 }}
          />
        </Autocomplete>
      </Box>

      <Box
        position="absolute"
        top="50%"
        left="50%"
        sx={{
          transform: "translate(-50%, -50%)",
          zIndex: 2,
          pointerEvents: "none",
        }}
      >
        <img src={target} alt="target" width={32} height={32} />
      </Box>

      <Box
        position="absolute"
        bottom={16}
        left="50%"
        sx={{ transform: "translateX(-50%)" }}
        zIndex={2}
      >
        <Stack direction="row" spacing={2}>
          <Fab color="default" size="medium" onClick={handleUndo}>
            <UndoIcon />
          </Fab>
          <Fab color="secondary" size="medium" onClick={goToMyLocation}>
            <MyLocationIcon />
          </Fab>
          <Fab color="primary" size="medium" onClick={handleAddPoint}>
            <AddIcon />
          </Fab>
          <Fab color="success" size="medium" onClick={handleConfirm}>
            <DoneIcon />
          </Fab>
        </Stack>
      </Box>
    </Box>
  );
}

export default MapDrawGoogle;
